import React from "react";

import { LeafIcon as Leaf } from "@packages/ds";
import momentjs from "moment";
import { useIntl } from "react-intl";

import Dialog from "ds/Dialog";
import { formatAsCurrency } from "Libs/utils";
import * as O from "src/organization/pages/billing/components/Overview/BillingOverview.styles";

import TableWrapper from "../Table";

import * as S from "./InvoiceProjects.styles";

import type { OrderLineItem } from "@packages/client";
import type { OrderLineItemsNormalized } from "src/organization/pages/billing/util";

type ProjectDetailTypes = {
  subscriptionId: string;
  lineItemsByOrder: OrderLineItemsNormalized;
  lineItemsProjectTotal: number;
  lineItemsProjectSubtotal: number;
  taxes?: { value: number; taxKey: string }[];
  discounts: { key: string; value: number }[];
};

const ProjectDetail = ({
  subscriptionId,
  lineItemsByOrder,
  lineItemsProjectTotal,
  lineItemsProjectSubtotal,
  taxes,
  discounts
}: ProjectDetailTypes) => {
  const intl = useIntl();

  const lineItems = lineItemsByOrder[subscriptionId].line_items;
  const durationDate = (date: string) => momentjs(date).format("MMM D");
  const orderCurrency = lineItems.flatMap(item =>
    Object.values(item.components)
      .map(component => component.currency)
      .filter(currency => typeof currency === "string")
  )[0];

  const isStandardUserManagementLine = (lineItem: OrderLineItem) =>
    typeof lineItem?.components?.standard_user_management_discount !==
    "undefined";
  const getComponentTitle = (title: string) =>
    lineItems.map(item => item.components?.[title]).filter(i => i)?.[0]
      ?.display_title;

  const projectName = lineItems[0].project;

  return (
    <S.ProjectDetailsWrapper>
      <TableWrapper
        headers={["Item", "Duration", "Unit price", "Usage", "Total"]}
        padding="10px 40px"
      >
        {lineItems.map((lineItem, index) => {
          if (
            lineItem.product === "Organization subtotal" ||
            lineItem.product === "Project subtotal"
          ) {
            return null;
          }

          return (
            <React.Fragment key={index}>
              <tr>
                <S.BigTD>
                  <S.TableCell className={lineItem.product.toLowerCase()}>
                    {lineItem.product}
                    {lineItem.custom_description && (
                      <small>{lineItem.custom_description}</small>
                    )}
                  </S.TableCell>
                </S.BigTD>

                <S.SmallTD>
                  <S.TableSmallCell>{`${durationDate(
                    lineItem.start
                  )} – ${durationDate(lineItem.end)}`}</S.TableSmallCell>
                </S.SmallTD>

                <S.SmallTD>
                  <S.TableSmallCell>
                    {isStandardUserManagementLine(lineItem) ? (
                      <s>
                        {formatAsCurrency(lineItem.unit_price, orderCurrency)}
                      </s>
                    ) : (
                      formatAsCurrency(lineItem.unit_price, orderCurrency)
                    )}
                  </S.TableSmallCell>
                </S.SmallTD>

                <S.SmallTD>
                  <S.TableSmallCell>
                    {(typeof lineItem.quantity === "string"
                      ? parseFloat(lineItem.quantity)
                      : lineItem.quantity
                    ).toFixed(2)}
                  </S.TableSmallCell>
                </S.SmallTD>

                <S.TD>
                  <S.TableTotalCell>
                    {formatAsCurrency(
                      lineItem?.components?.base_price.amount,
                      orderCurrency
                    )}
                  </S.TableTotalCell>
                </S.TD>
              </tr>
            </React.Fragment>
          );
        })}
      </TableWrapper>
      <S.ProjectDetailsSubtotal>
        {intl.formatMessage(
          {
            id: `billing.invoice.project_subtotal`
          },
          { projectName: projectName }
        )}
        <div>{formatAsCurrency(lineItemsProjectSubtotal, orderCurrency)}</div>
      </S.ProjectDetailsSubtotal>

      {discounts.map(discount => (
        <S.ProjectDetailsComponent key={discount.key}>
          <S.TitleIconWrapper>
            {getComponentTitle(discount.key)}
            {discount.key === "green_discount" && (
              <Dialog
                analyticId="green_discount.description"
                trigger={
                  <O.IconWrapper aria-label="leaf">
                    <Leaf color="var(--theme-success-600)" />
                  </O.IconWrapper>
                }
                title={intl.formatMessage({
                  id: "green_discount.title"
                })}
                description={intl.formatMessage({
                  id: "green_discount.description"
                })}
              />
            )}
          </S.TitleIconWrapper>
          {discount.key === "green_discount" && (
            <div>
              {intl.formatMessage({
                id: `billing.invoice.project_green_discount_description`
              })}
            </div>
          )}
          <div>{formatAsCurrency(discount.value, orderCurrency)}</div>
        </S.ProjectDetailsComponent>
      ))}
      {taxes?.map(tax => (
        <S.ProjectDetailsComponent key={tax.taxKey}>
          {getComponentTitle(tax.taxKey)}
          <div>{formatAsCurrency(tax.value, orderCurrency)}</div>
        </S.ProjectDetailsComponent>
      ))}
      <S.ProjectDetailsSubtotal>
        {intl.formatMessage(
          {
            id: `billing.invoice.project_total`
          },
          { projectName: projectName }
        )}
        <div>{formatAsCurrency(lineItemsProjectTotal, orderCurrency)}</div>
      </S.ProjectDetailsSubtotal>
    </S.ProjectDetailsWrapper>
  );
};

export default ProjectDetail;
